import { ProductCatalogStorefrontStatus } from '@graphql/generated-base';
import { SkeletonText } from '@shared/components/Skeleton';
import type { BoxProps } from '@withjoy/joykit';
import { Box, ButtonV2, Flex, FormField, InputV2, Portal } from '@withjoy/joykit';
import React, { useState, useRef, useEffect } from 'react';
import { useCatalogRegistryTranslations } from '../../../../Catalog.i18n';
import { ProductQuickAddBar } from '../ProductQuickAddBar';
import { ProductStorefrontStatusBadge } from '../ProductStorefrontStatusBadge';
import { StickyProductQuickAddBar } from '../StickyProductQuickAddBar/StickyProductQuickAddBar';
import { ReactComponent as MinusIcon } from '@assets/icons/icon-minus.svg';
import { ReactComponent as PlusIcon } from '@assets/icons/icon-plus.svg';
import {
  addedToRegistryStyles,
  loadingBackground,
  registryPrimaryButtonStyles,
  registrySecondaryButtonStyles,
  purchaseUnavailableButtonStyles,
  StyledButton,
  StyledButtonWrapper,
  StyledFlex,
  StyledForm,
  StyledFormBody,
  StyledFormInputFieldContainer,
  StyledQuantityToggleContainer,
  StyledQuickAddFormFlex,
  styles,
} from './BuyForm.styles';
import type { BuyFormProps, BuyFormSkeletonProps, DefaultBuyFormCtaProps, QuantityStepperProps, QuickAddFormCtaProps } from './BuyForm.types';
import { AddCatalogItemPurchaseMethod } from './BuyForm.types';
import { useBuyForm } from './useBuyForm';
import { useDimensionAvailability } from './useDimensionAvailability';
import { BuyFormDimensionsList } from '../BuyFormDimension';
import { ReactComponent as DoneCheck } from '@assets/icons/circle-check-white.svg';
import { useOnIntersectingChange } from '@shared/utils/hooks/useOnIntersectingChange';
import { useShippingAddressAndCurrencyDialogsContext } from '@apps/registry/common/components/ShippingAddressAndCurrencyDialogsProvider';
import { useTranslation } from '@shared/core';

enum BuyFormButtonName {
  addToRegistry = 'addToRegistry',
  addToCart = 'addToCart',
  buyNow = 'buyNow',
}

const PurchaseUnavailableButton = () => {
  const { tq } = useTranslation('catalogRegistry');

  return (
    <StyledButton
      shape="rounded"
      overrides={purchaseUnavailableButtonStyles}
      disabled
    >
      {tq`Unavailable`}
    </StyledButton>
  );
};

const QuantityStepper = ({ setFieldValue, qty, getFieldProps }: QuantityStepperProps) => {
  return (
    <StyledQuantityToggleContainer>
      <ButtonV2
        startIcon={<MinusIcon />}
        variant="ghost"
        onClick={() => setFieldValue('qty', --qty)}
        disabled={qty === 1}
      />
      <InputV2
        type="number"
        {...getFieldProps('qty')}
        min="1"
        pattern="[0-9]*"
      />
      <ButtonV2
        startIcon={<PlusIcon />}
        variant="ghost"
        intent="neutral"
        onClick={() => setFieldValue('qty', ++qty)}
      />
    </StyledQuantityToggleContainer>
  );
};

const QuickAddFormCtas = ({
  quantity,
  maxQuantity,
  qtyError,
  getFieldProps,
  setFieldValue,
  onAddCatalogItem,
  handleMouseMove,
  isAddingItem,
  isFormValid,
  showQuantity,
  buyNowCopy,
  addToCartCopy,
  storefrontInfo,
  isItemAddedToRegistry,
  setOpenGiftDialog,
  isOpenGiftDialog,
  buttonText,
  isButtonHovered,
  handleOnMouseHover,
  handleMouseLeave,
  isMobile,
}: QuickAddFormCtaProps & { isOpenGiftDialog?: boolean; isMobile?: boolean }) => {
  const [loadingButton, setLoadingButton] = useState<BuyFormButtonName | null>(null);
  const qtyNumber = Number(getFieldProps('qty').value);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [ctaInView, setCtaInView] = useState(true);
  const stickyButtonRef = useRef<HTMLButtonElement>(null);

  useOnIntersectingChange(
    buttonRef,
    (isIntersecting: boolean) => {
      if (!isOpenGiftDialog) {
        setCtaInView(isIntersecting);
      }
    },
    { threshold: 0 },
  );

  const handleAddToRegistry = () => {
    setCtaInView(true);
    if (isItemAddedToRegistry) {
      setOpenGiftDialog(true);
    } else {
      setLoadingButton(BuyFormButtonName.addToRegistry);
      onAddCatalogItem();
    }
  };

  const isLoading = isAddingItem && loadingButton === BuyFormButtonName.addToRegistry;

  const buttonStyles = {
    ...registryPrimaryButtonStyles.Root,
    props: {
      ...registryPrimaryButtonStyles.Root?.props,
      _disabled: isLoading ? loadingBackground : { backgroundImage: 'none' },
      ...(isItemAddedToRegistry && addedToRegistryStyles),
    },
  };

  const hasInventory = !!maxQuantity;
  const insufficientInventory = qtyNumber > (maxQuantity ?? 0);
  const isPurchaseButtonDisabled = !isFormValid || isAddingItem || insufficientInventory;
  const { isJoyCheckoutUnavailable } = useShippingAddressAndCurrencyDialogsContext();
  const canPurchase = hasInventory && !!storefrontInfo?.canAddToCart;

  return (
    <Flex flexDirection="column">
      {showQuantity && !isItemAddedToRegistry && (
        <StyledFormInputFieldContainer marginBottom={5}>
          <FormField
            label={quantity}
            hideLabel={true}
            error={qtyError}
            css={{ width: '100%' }}
          >
            <QuantityStepper
              setFieldValue={setFieldValue}
              qty={qtyNumber}
              getFieldProps={getFieldProps}
            />
          </FormField>
        </StyledFormInputFieldContainer>
      )}
      <StyledButtonWrapper
        stack="vertical"
        spacing={5}
      >
        <ButtonV2
          ref={buttonRef}
          onClick={handleAddToRegistry}
          loading={isLoading}
          shape="rounded"
          startIcon={isItemAddedToRegistry && !isButtonHovered ? <DoneCheck iconSize={20} /> : null}
          disabled={!isFormValid || isAddingItem || !storefrontInfo?.canAddToRegistry}
          overrides={{ Root: buttonStyles }}
          onMouseEnter={isItemAddedToRegistry ? handleOnMouseHover : undefined}
          onMouseLeave={isItemAddedToRegistry ? handleMouseLeave : undefined}
          onMouseMove={e => handleMouseMove(e, buttonRef)}
        >
          {buttonText}
        </ButtonV2>
        {canPurchase &&
          (isJoyCheckoutUnavailable ? (
            <PurchaseUnavailableButton />
          ) : (
            <>
              <ButtonV2
                overrides={registrySecondaryButtonStyles}
                loading={isAddingItem && loadingButton === BuyFormButtonName.addToCart}
                shape="rounded"
                onClick={() => {
                  setLoadingButton(BuyFormButtonName.addToCart);
                  onAddCatalogItem({ purchaseMethod: AddCatalogItemPurchaseMethod.addToCart });
                }}
                variant="outline"
                disabled={isPurchaseButtonDisabled}
              >
                {addToCartCopy}
              </ButtonV2>
              <ButtonV2
                shape="rounded"
                overrides={registrySecondaryButtonStyles}
                onClick={() => {
                  setLoadingButton(BuyFormButtonName.buyNow);
                  onAddCatalogItem({ purchaseMethod: AddCatalogItemPurchaseMethod.buyNow });
                }}
                variant="outline"
                loading={isAddingItem && loadingButton === BuyFormButtonName.buyNow}
                disabled={isPurchaseButtonDisabled}
              >
                {buyNowCopy}
              </ButtonV2>
            </>
          ))}
      </StyledButtonWrapper>
      {(() => {
        const shouldShow = !ctaInView && !isOpenGiftDialog && isMobile;
        return shouldShow ? (
          <Portal>
            <Box {...styles.stickyButtonWrapper}>
              <ButtonV2
                ref={stickyButtonRef}
                onClick={handleAddToRegistry}
                loading={isLoading}
                shape="rounded"
                fullWidth
                startIcon={isItemAddedToRegistry && !isButtonHovered ? <DoneCheck iconSize={20} /> : null}
                disabled={!isFormValid || isAddingItem || !storefrontInfo?.canAddToRegistry}
                overrides={{ Root: buttonStyles }}
                onMouseEnter={isItemAddedToRegistry ? handleOnMouseHover : undefined}
                onMouseLeave={isItemAddedToRegistry ? handleMouseLeave : undefined}
                onMouseMove={e => handleMouseMove(e, stickyButtonRef)}
              >
                {buttonText}
              </ButtonV2>
            </Box>
          </Portal>
        ) : null;
      })()}
    </Flex>
  );
};

const DefaultBuyFormCtas = React.forwardRef<HTMLDivElement, DefaultBuyFormCtaProps>(
  (
    {
      quantity,
      maxQuantity,
      qtyError,
      getFieldProps,
      setFieldValue,
      onAddCatalogItem,
      isAddingItem,
      isFormValid,
      buyNowCopy,
      addToCartCopy,
      storefrontInfo,
      isItemAddedToRegistry,
      handleMouseMove,
      setOpenGiftDialog,
      buttonText,
      isButtonHovered,
      handleOnMouseHover,
      handleMouseLeave,
    },
    ref,
  ) => {
    const [loadingButton, setLoadingButton] = useState<BuyFormButtonName | null>(null);
    const qtyNumber = Number(getFieldProps('qty').value);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const hasInventory = !!maxQuantity;
    const insufficientInventory = qtyNumber > (maxQuantity ?? 0);
    const isPurchaseButtonDisabled = !isFormValid || isAddingItem || insufficientInventory;
    const { isJoyCheckoutUnavailable } = useShippingAddressAndCurrencyDialogsContext();
    const canPurchase = hasInventory && !!storefrontInfo?.canAddToCart;

    const handleAddToRegistry = () => {
      if (isItemAddedToRegistry) {
        setOpenGiftDialog(true);
      } else {
        setLoadingButton(BuyFormButtonName.addToRegistry);
        onAddCatalogItem();
      }
    };

    const isLoading = isAddingItem && loadingButton === BuyFormButtonName.addToRegistry;

    const buttonStyles = {
      ...registryPrimaryButtonStyles.Root,
      props: {
        ...registryPrimaryButtonStyles.Root?.props,
        _disabled: isLoading ? loadingBackground : { backgroundImage: 'none' },
        ...(isItemAddedToRegistry && addedToRegistryStyles),
      },
    };

    return (
      <Flex
        ref={ref}
        flexDirection="column"
      >
        <StyledFormInputFieldContainer marginBottom={5}>
          {!isItemAddedToRegistry && (
            <FormField
              label={quantity}
              error={qtyError}
              css={{ width: '100%' }}
            >
              <QuantityStepper
                setFieldValue={setFieldValue}
                qty={qtyNumber}
                getFieldProps={getFieldProps}
              />
            </FormField>
          )}
        </StyledFormInputFieldContainer>
        <StyledButtonWrapper
          stack="vertical"
          spacing={5}
        >
          {storefrontInfo?.storefrontStatus !== ProductCatalogStorefrontStatus.available && (
            <ProductStorefrontStatusBadge
              storefrontStatus={storefrontInfo?.storefrontStatus}
              variant="inline"
            />
          )}
          <StyledButton
            ref={buttonRef}
            onClick={handleAddToRegistry}
            loading={isLoading}
            shape="rounded"
            startIcon={isItemAddedToRegistry && !isButtonHovered ? <DoneCheck iconSize={20} /> : null}
            disabled={!isFormValid || isAddingItem || !storefrontInfo?.canAddToRegistry}
            overrides={{ Root: buttonStyles }}
            onMouseEnter={isItemAddedToRegistry ? handleOnMouseHover : undefined}
            onMouseLeave={isItemAddedToRegistry ? handleMouseLeave : undefined}
            onMouseMove={(e: React.MouseEvent<HTMLButtonElement>) => handleMouseMove(e, buttonRef)}
          >
            {buttonText}
          </StyledButton>
          {canPurchase &&
            (isJoyCheckoutUnavailable ? (
              <PurchaseUnavailableButton />
            ) : (
              <>
                <StyledButton
                  shape="rounded"
                  overrides={registrySecondaryButtonStyles}
                  onClick={() => {
                    setLoadingButton(BuyFormButtonName.addToCart);
                    onAddCatalogItem({ purchaseMethod: AddCatalogItemPurchaseMethod.addToCart });
                  }}
                  loading={isAddingItem && loadingButton === BuyFormButtonName.addToCart}
                  variant="outline"
                  disabled={isPurchaseButtonDisabled}
                >
                  {addToCartCopy}
                </StyledButton>
                <StyledButton
                  shape="rounded"
                  overrides={registrySecondaryButtonStyles}
                  onClick={() => {
                    setLoadingButton(BuyFormButtonName.buyNow);
                    onAddCatalogItem({ purchaseMethod: AddCatalogItemPurchaseMethod.buyNow });
                  }}
                  variant="outline"
                  loading={isAddingItem && loadingButton === BuyFormButtonName.buyNow}
                  disabled={isPurchaseButtonDisabled}
                >
                  {buyNowCopy}
                </StyledButton>
              </>
            ))}
        </StyledButtonWrapper>
      </Flex>
    );
  },
);

export const BuyForm = ({
  isAddingItem,
  isMobile,
  isQuickAddForm,
  showQuantity = true,
  showAllDimensions,
  hideDiscontinuedSwatches,
  relatedItems,
  product,
  selectedProduct,
  setProductSelected,
  addCatalogItem,
  itemPosition,
  colorSwatchVariant,
  dimensionSpacing,
  selectedImage,
  showSkeleton,
  stickyBarIntersectionRef,
  galleryLightboxQuickAddBarRef,
  handleHover = () => {},
  isItemAddedToRegistry,
  setOpenGiftDialog,
  isOpenGiftDialog,
}: BuyFormProps & { isOpenGiftDialog?: boolean }) => {
  const { getProductDetailPageTranslations, getAddProductTranslations } = useCatalogRegistryTranslations();
  const { quantity, buyNow: buyNowCopy, buttonsDividerText } = getProductDetailPageTranslations();
  const { addToRegistry: addToRegistryCopy, addToCart: addToCartCopy, addedToRegistry, editGift } = getAddProductTranslations();
  const { storefrontInfo, maxQty: maxQuantity } = product;

  const { dimensions, dimensionFormValues, formik, setDimensionOption, handleAddCatalogItem, handleOnDimensionOptionChangeOnHover } = useBuyForm({
    product,
    selectedProduct,
    addCatalogItem,
    isQuickAddForm,
    setProductSelected,
    itemPosition,
    relatedItems,
    hideDiscontinuedSwatches,
  });

  const getFieldProps = formik.getFieldProps;
  const setFieldValue = formik.setFieldValue;
  const isFormValid = formik.isValid;
  const qtyError = formik.errors.qty;

  const { checkOptionAvailability } = useDimensionAvailability({
    relatedItems,
    dimensionFormValues,
  });

  const shouldHideDiscontinuedOptions = hideDiscontinuedSwatches === true && dimensions.length === 1;

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>, buttonRef: React.RefObject<HTMLButtonElement>) => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const x = ((e.clientX - rect.left) * 100) / button.clientWidth;
    const y = ((e.clientY - rect.top) * 100) / button.clientHeight;

    button.style.setProperty('--mouse-x', `${x}%`);
    button.style.setProperty('--mouse-y', `${y}%`);
  };

  const [buttonText, setButtonText] = useState(isItemAddedToRegistry ? addedToRegistry : addToRegistryCopy);
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  const handleOnMouseHover = () => {
    setButtonText(editGift);
    setIsButtonHovered(true);
  };

  const handleMouseLeave = () => {
    setButtonText(addedToRegistry);
    setIsButtonHovered(false);
  };

  useEffect(() => {
    if (!isOpenGiftDialog && isItemAddedToRegistry) {
      setButtonText(addedToRegistry);
      setIsButtonHovered(false);
    }
  }, [isOpenGiftDialog, isItemAddedToRegistry, addedToRegistry]);

  return (
    <>
      {stickyBarIntersectionRef && (
        <StickyProductQuickAddBar
          isMobile={isMobile}
          isAddingItem={isAddingItem}
          handleAddCatalogItem={handleAddCatalogItem}
          showSkeleton={showSkeleton}
          selectedProduct={selectedProduct}
          selectedImage={selectedImage}
          intersectionRef={stickyBarIntersectionRef}
        ></StickyProductQuickAddBar>
      )}
      <StyledForm
        as="form"
        marginTop={dimensionSpacing}
        marginBottom={[dimensionSpacing, null, 0]}
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
        }}
      >
        <StyledFormBody spacing={dimensionSpacing}>
          {!isItemAddedToRegistry && (
            <BuyFormDimensionsList
              key={product.id}
              dimensions={dimensions}
              colorSwatchVariant={colorSwatchVariant}
              showAllDimensions={showAllDimensions}
              isQuickAddForm={isQuickAddForm}
              getFieldProps={getFieldProps}
              setDimensionOption={setDimensionOption}
              checkOptionAvailability={checkOptionAvailability}
              shouldHideDiscontinuedOptions={shouldHideDiscontinuedOptions}
              handleHover={(optionValue: string, updatedField: string, isHoverExit?: boolean) =>
                handleHover(handleOnDimensionOptionChangeOnHover(optionValue, updatedField), isHoverExit)
              }
            />
          )}
          {!isQuickAddForm ? (
            <DefaultBuyFormCtas
              quantity={quantity}
              maxQuantity={maxQuantity}
              qtyError={qtyError}
              getFieldProps={getFieldProps}
              setFieldValue={setFieldValue}
              onAddCatalogItem={handleAddCatalogItem}
              isAddingItem={isAddingItem}
              isFormValid={isFormValid}
              buyNowCopy={buyNowCopy}
              addToRegistryCopy={addToRegistryCopy}
              addToCartCopy={addToCartCopy}
              storefrontInfo={storefrontInfo}
              buttonsDividerText={buttonsDividerText}
              ref={stickyBarIntersectionRef}
              handleMouseMove={handleMouseMove}
              isItemAddedToRegistry={isItemAddedToRegistry}
              addedToRegistry={addedToRegistry}
              editGift={editGift}
              setOpenGiftDialog={setOpenGiftDialog}
              buttonText={buttonText}
              isButtonHovered={isButtonHovered}
              handleOnMouseHover={handleOnMouseHover}
              handleMouseLeave={handleMouseLeave}
            />
          ) : (
            <QuickAddFormCtas
              quantity={quantity}
              maxQuantity={maxQuantity}
              qtyError={qtyError}
              getFieldProps={getFieldProps}
              setFieldValue={setFieldValue}
              onAddCatalogItem={handleAddCatalogItem}
              isAddingItem={isAddingItem}
              isFormValid={isFormValid}
              buyNowCopy={buyNowCopy}
              showQuantity={showQuantity}
              addToRegistryCopy={addToRegistryCopy}
              addToCartCopy={addToCartCopy}
              storefrontInfo={storefrontInfo}
              handleMouseMove={handleMouseMove}
              isItemAddedToRegistry={isItemAddedToRegistry}
              addedToRegistry={addedToRegistry}
              editGift={editGift}
              setOpenGiftDialog={setOpenGiftDialog}
              buttonText={buttonText}
              isButtonHovered={isButtonHovered}
              handleOnMouseHover={handleOnMouseHover}
              handleMouseLeave={handleMouseLeave}
              isOpenGiftDialog={isOpenGiftDialog}
              isMobile={isMobile}
            />
          )}
        </StyledFormBody>
      </StyledForm>
      {galleryLightboxQuickAddBarRef && (
        // this is the top bar of the GalleryLightbox,
        // when the PdpGalleryLightbox opens the display property will change
        <Portal>
          <Box
            ref={galleryLightboxQuickAddBarRef}
            display={'none'}
          >
            <ProductQuickAddBar
              brand={selectedProduct?.brand?.name}
              name={selectedProduct?.title}
              priceFloatingPointDecimalString={selectedProduct?.price?.floatingPointDecimalString}
              priceCurrencyCode={selectedProduct?.price?.currency?.code}
              storefrontInfo={selectedProduct?.storefrontInfo}
              isMobile={isMobile}
              isAddingItem={isAddingItem}
              onAddCatalogItem={() => handleAddCatalogItem()}
              showSkeleton={showSkeleton}
              backgroundColor={'white'}
              zIndex={10000}
              position={'fixed'}
              right={0}
              top={!isMobile ? 0 : null}
              bottom={isMobile ? 0 : null}
            />
          </Box>
        </Portal>
      )}
    </>
  );
};

BuyForm.Skeleton = ({ mode = 'full', ...restProps }: BuyFormSkeletonProps & BoxProps) => {
  const { isQuickAddForm } = restProps;
  return (
    <StyledForm
      as="form"
      {...restProps}
    >
      {!isQuickAddForm ? (
        <>
          <StyledFlex>
            <Box
              data-qty
              display="inline-flex"
              flexDirection="column"
              width={62}
              marginRight={30}
            >
              <SkeletonText
                rows={1}
                skeletonHeight={'12px'}
                width={42}
              />
              <SkeletonText
                rows={1}
                skeletonHeight={36}
                marginTop={4}
                width={260}
              />
              <SkeletonText
                rows={1}
                skeletonHeight={'12px'}
                marginTop="42px"
              />
              <SkeletonText
                rows={1}
                skeletonHeight={36}
                marginTop={4}
                width={150}
              />
            </Box>
          </StyledFlex>
          <Box
            data-ctas
            display="flex"
            flexDirection="column"
            justifyContent="center"
            width="100%"
          >
            <SkeletonText
              rows={2}
              enableVaryingRowWidth={true}
              skeletonHeight={40}
              rowWidths={mode === 'full' ? [60, 60] : [90, 90]}
            />
            <SkeletonText
              rows={1}
              skeletonHeight={20}
              width="100px"
              marginTop={4}
            />
          </Box>
        </>
      ) : (
        <>
          <StyledQuickAddFormFlex __css={styles.quickAddFormPlaceHolder}>
            <Box
              data-qty
              display="inline-flex"
              flexDirection="column"
              width={62}
              marginRight={30}
            >
              <SkeletonText
                rows={1}
                skeletonHeight={'18px'}
                width={42}
              />
              <SkeletonText
                rows={1}
                skeletonHeight={36}
                marginTop={4}
                width="100%"
              />
            </Box>
          </StyledQuickAddFormFlex>
          <Box
            data-ctas
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
            marginTop={5}
          >
            <SkeletonText
              rows={1}
              skeletonHeight={40}
              width="50px"
              marginRight={3}
            />
            <SkeletonText
              rows={1}
              skeletonHeight={40}
              __css={{ flex: 1 }}
            />
          </Box>
        </>
      )}
    </StyledForm>
  );
};
